// "That barcode isn't on this order" — what the desk should say instead.
//
// A pack scan that matches nothing used to produce one message for every
// cause: `item_not_in_order_number`. To a packer that reads as either
// "the scanner is broken" or "this item is wrong", and both readings end
// with a flagged order. The two Pembroke flags on 2026-08-19 were the
// same mis-sort seen from both ends — a unit sorted into the neighbouring
// slot — and nothing on screen said so.
//
// Two layers, in this order:
//   1. classifyUnknownScan — instant, local, no network. Some codes are
//      not product barcodes at all and never will be, so looking them up
//      is pointless and "not in this order" actively misleads.
//   2. the /dispatch/identify-barcode lookup (caller's job) — for codes
//      that could plausibly be a product, name it and say where it belongs.

export type UnknownScanKind =
    // Amazon Transparency: "AZ:" + a per-unit-unique code. It identifies
    // THE UNIT, not the product, so it can never be enrolled as a barcode
    // and will fail forever. The item's own barcode/FNSKU is elsewhere on
    // the packaging (order 336795, 2026-08-19: scanned twice, then the
    // line was marked missing).
    | "transparency"
    // A URL from a marketing QR code on the packaging (336795 again:
    // "http://www.taiwancarol.com").
    | "url"
    // Carrier / logistics routing barcodes — GS1-128 application
    // identifiers and the tilde-delimited parcel strings couriers print.
    // These belong to the box, not the product.
    | "logistics"
    // Nothing distinctive — could be a real product barcode we don't
    // hold, or a genuinely wrong item. Worth a catalogue lookup.
    | "unknown";

export const classifyUnknownScan = (raw: string): UnknownScanKind => {
    const value = String(raw ?? "").trim();
    if (!value) return "unknown";
    if (/^AZ:/i.test(value)) return "transparency";
    if (/^(https?:\/\/|www\.)/i.test(value)) return "url";
    // A tilde-delimited routing string, or a GS1-128 SSCC/serial block
    // ("M01" + 18 digits is the shape Royal Mail / DPD print). Length is
    // part of the test: a bare 13-digit EAN must stay "unknown".
    if (value.includes("~")) return "logistics";
    if (/^[A-Z]\d{2}\d{15,}$/i.test(value)) return "logistics";
    return "unknown";
};

/** i18n key for the toast shown for each non-product class. */
export const unknownScanMessageKey = (kind: UnknownScanKind): string | null => {
    switch (kind) {
        case "transparency": return "scan_is_transparency_label";
        case "url":          return "scan_is_url_qr";
        case "logistics":    return "scan_is_logistics_label";
        default:             return null;
    }
};

/** Does this class justify a catalogue round trip? */
export const shouldIdentify = (kind: UnknownScanKind): boolean => kind === "unknown";

export type IdentifiedBarcode = {
    found: boolean;
    sku?: string;
    title?: string;
    orders?: {
        order_id: string;
        order_number: string;
        status: string;
        warehouse: string;
        dispatch_tote_id: string | null;
    }[];
};

/**
 * Build the "you scanned X, it belongs to Y" line from a lookup result.
 * Returns null when there is nothing more useful to say than the generic
 * message the caller already showed.
 *
 * `t` is the caller's translator so this stays testable without a
 * provider, and the copy stays in the locale files.
 */
export const describeIdentifiedScan = (
    result: IdentifiedBarcode | null,
    t: (key: string, vars?: Record<string, string | number>) => string,
): string | null => {
    if (!result?.found) return null;
    const label = result.title?.trim() || result.sku || "";
    if (!label) return null;
    // The order it belongs to is the actionable half — prefer a still-open
    // one, but a recently-fulfilled order is the mis-sort case and matters
    // MORE, not less: that order has already gone out one item short.
    const other = result.orders?.[0];
    if (!other) return t("scan_belongs_to_no_order", { item: label });
    if (other.dispatch_tote_id) {
        return t("scan_belongs_to_order_tote", {
            item: label, order: other.order_number, tote: other.dispatch_tote_id,
        });
    }
    return t("scan_belongs_to_order", { item: label, order: other.order_number });
};
